import React, { useContext, useEffect, useState } from 'react';
import { readCookie } from '../../utils/cookie-helpers';
import * as Sentry from '@sentry/react';
import { ActiveTest, IabTests, activeTests } from './activeTests';
import { isClientSide } from '../../helpers/IsomorphicHelpers';

export const AbTestsContext = React.createContext<IabTests | undefined>(undefined);

const getAbTestsFromCookie = (): Record<string, { v: string; g: string }> => {
    const convertTestValue = readCookie('_conv_v') || '{}';
    const experiments = convertTestValue.split('*')?.[5]?.replace('exp:', '');

    if (!experiments) {
        return {};
    }

    const normilizedValue = experiments
        .replace(/-/g, ',')
        .replace(/\./g, ':')
        .replace(/([a-z0-9]+):/g, '"$1":');

    try {
        return JSON.parse(normilizedValue);
    } catch (error) {
        Sentry.captureException(new Error(`Could not parse AB test data from cookie: ${error}`));
        return {};
    }
};

const stringifyABtests = (tests: ActiveTest[]) =>
    tests.reduce((acc: any, test: any) => {
        return acc.concat(
            acc ? ', ' : '',
            `${test.name}_${test.variationGroup === test.newVariationId ? 'on' : 'off'}`,
        );
    }, '');

const exposeTestsToInfinity = (activeTestData: ActiveTest[]) => {
    const stringifiedTests = stringifyABtests(activeTestData);
    if (isClientSide()) {
        (window as any)['infinityTests'] = stringifiedTests;
    }
};

const exposeTestsToDataLayer = (activeTestData: ActiveTest[]) => {
    const stringifiedTests = stringifyABtests(activeTestData);
    if (isClientSide()) {
        (window as any).dataLayer.push({ event: 'ab-test-available', abTests: stringifiedTests });
    }
};

export const initABTests = (): IabTests => {
    let allExperiments: { [key: string]: { variation_id?: string | undefined } } = {};

    if (isClientSide()) {
        allExperiments = (window as any)?.convert?.currentData?.experiments;
    } else {
        const ssrExperiments = getAbTestsFromCookie();
        allExperiments = Object.entries(ssrExperiments).reduce(
            (acc, [key, value]) => ({ ...acc, [key]: { variation_id: value?.v } }),
            {},
        );
    }

    const activeTestData = activeTests.map((testData) => {
        const cookieValue = JSON.parse(readCookie('override_test') || '{}');
        const originalVariationGroup = allExperiments?.[testData.testId]?.variation_id;
        const overridenVariationGroup = cookieValue?.[testData.testId];

        const variationGroup =
            overridenVariationGroup !== undefined
                ? overridenVariationGroup
                : originalVariationGroup;
        return {
            ...testData,
            variationGroup: Number.parseInt(variationGroup, 10),
        };
    });

    exposeTestsToInfinity(activeTestData);
    exposeTestsToDataLayer(activeTestData);

    if (!activeTestData.length) return {};

    const newState = activeTestData.reduce((accumulator: any, currentValue: any) => {
        return { ...accumulator, [currentValue.name]: currentValue };
    }, {});

    return { ...newState };
};

export const manuallyTriggerConvertABtest = (newId: number, testId: number) => {
    if (isClientSide()) {
        (window as any)._conv_q = (window as any)._conv_q || [];

        (window as any)._conv_q.push(['assignVariation', testId, newId]);
        (window as any)._conv_q.push(['run', 'true']);
    }
};

export const AbTestsProvider = ({
    children,
    value,
}: {
    children: JSX.Element;
    value: IabTests;
}) => {
    const [testsValue, setTestsValue] = useState(value);

    useEffect(() => {
        setTestsValue(initABTests());
    }, []);

    return <AbTestsContext.Provider value={{ ...testsValue }}>{children}</AbTestsContext.Provider>;
};

export const useAbTest = (): IabTests => {
    const context = useContext(AbTestsContext);

    if (!context) {
        throw new Error('must be used within a AbTestProvider');
    }

    return context;
};
